import React, { useReducer, useEffect } from "react"
import classNames from "classnames"
import PropTypes from "prop-types"
import CoreFilters from "./CoreFilters"
import AdvancedFilters from "./AdvancedFilters"
import Button from "../../Button/Button"
import { ReactComponent as ArrowTopIcon } from "../../../images/icons/arrow-top.svg"
import { reducer, initState } from "./useReducer/reducer"
import {
  toggleCollapse,
  setWholeState,
  setMode,
  resetForm,
} from "./useReducer/actionCreators"
import {
  CORE,
  ADVANCED,
  countAppliedFilters,
  toSearchParams,
} from "./filterConfig"
import styles from "./index.module.scss"

const MOBILE_QUERY = "(max-width: 768px)"

const TABS = [
  {
    id: CORE,
    label: "Core Filters",
    subtitle: "Core filters for a fast, relevant search",
    note: [
      "The essential filters to help you quickly find relevant opportunities.",
    ],
  },
  {
    id: ADVANCED,
    label: "Advanced Filters",
    subtitle: "Advanced filters for a more personalized search",
    note: [
      "Advanced filters to refine your results and get more personalized recommendations.",
      "These filters help us learn your preferences and deliver smarter opportunities over time.",
    ],
  },
]

// Expanded on desktop, collapsed on mobile.
const initCollapse = (state) => {
  try {
    return { ...state, isCollapse: window.matchMedia(MOBILE_QUERY).matches }
  } catch (e) {
    return state
  }
}

const BrandMark = () => (
  <div className={styles.brandMark}>
    <svg
      width="34"
      height="30"
      viewBox="0 0 34 30"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M1 29h32M5 29V19h5v10M14 29V12h5v17M23 29V5h5v24M24 4l5-3"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
    <span>
      More opportunities.
      <br />A brighter tomorrow.
    </span>
  </div>
)

const OpportunitySearchFilter = ({
  classes,
  searchParamGroup,
  onReset,
  onSubmit,
  onModeChange,
}) => {
  const [state, dispatch] = useReducer(reducer, initState, initCollapse)
  useEffect(() => {
    dispatch(setWholeState(searchParamGroup))
  }, [searchParamGroup])

  const { mode, values } = state
  const activeTab = TABS.find((tab) => tab.id === mode)
  const selectedCount = countAppliedFilters(mode, values)
  const hasInvalidValueRange =
    mode === CORE &&
    values.valueMin !== "" &&
    values.valueMax !== "" &&
    Number(values.valueMin) > Number(values.valueMax)

  const selectMode = (nextMode) => {
    if (nextMode === mode) return
    dispatch(setMode(nextMode))
    onModeChange(nextMode)
  }

  return (
    <section
      className={classNames(styles.filter, classes, {
        [styles.isCollapse]: state.isCollapse,
        [styles.advanced]: mode === ADVANCED,
      })}
      aria-label="Opportunity filters"
    >
      <div className={styles.header}>
        <p className={styles.subtitle}>{activeTab.subtitle}</p>
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={!state.isCollapse}
          onClick={() => dispatch(toggleCollapse())}
        >
          All Filters
          <ArrowTopIcon className={styles.toggleIcon} />
        </button>
      </div>
      <div className={styles.form}>
        <div className={styles.tabs} role="tablist" aria-label="Filter type">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`filter-tab-${tab.id}`}
              aria-selected={mode === tab.id}
              aria-controls="filter-tabpanel"
              className={classNames(styles.tab, {
                [styles.activeTab]: mode === tab.id,
              })}
              onClick={() => selectMode(tab.id)}
            >
              {tab.label}
              {mode === tab.id && selectedCount > 0 && (
                <span className={styles.tabCount}>{selectedCount}</span>
              )}
            </button>
          ))}
        </div>
        <div className={styles.card}>
          <div
            className={styles.body}
            role="tabpanel"
            id="filter-tabpanel"
            aria-labelledby={`filter-tab-${mode}`}
          >
            {mode === CORE ? (
              <CoreFilters values={values} dispatch={dispatch} />
            ) : (
              <AdvancedFilters values={values} dispatch={dispatch} />
            )}
          </div>
          <div className={styles.footer}>
            <Button
              buttonStyle="outlined"
              className={styles.clearBtn}
              onClick={() => {
                dispatch(resetForm())
                onReset()
              }}
            >
              Clear All
            </Button>
            <Button
              className={styles.applyBtn}
              disabled={hasInvalidValueRange}
              onClick={() => onSubmit(toSearchParams(mode, values))}
            >
              Apply Filters
            </Button>
          </div>
        </div>
        <div className={styles.note}>
          <p>
            {activeTab.note.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </p>
          {mode === ADVANCED && <BrandMark />}
        </div>
      </div>
    </section>
  )
}

OpportunitySearchFilter.propTypes = {
  classes: PropTypes.string,
  searchParamGroup: PropTypes.object.isRequired,
  /** Clears every applied filter */
  onReset: PropTypes.func.isRequired,
  /** Receives the search params of the active tab only */
  onSubmit: PropTypes.func.isRequired,
  /** Called when switching between Core and Advanced */
  onModeChange: PropTypes.func,
}

OpportunitySearchFilter.defaultProps = {
  onModeChange: () => {},
}

export default OpportunitySearchFilter
